import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';

import { FormComponentBuilder } from 'components/common/form/layout';
import FormText from 'components/common/form/layout/control/FormText';
import React, { useEffect, useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import { getElementFieldValue } from 'components/common/form/formTools';

export type ExternalReportObjectNamePatternPlugin = 'tableau' | 'oac';
const ALLOWED_PLUGINS: ExternalReportObjectNamePatternPlugin[] = ['tableau', 'oac'];
const DEFAULT_PLUGIN: ExternalReportObjectNamePatternPlugin = 'tableau';

export interface ExternalReportObjectNamePatternProps extends FormControlProps {
    plugin: ExternalReportObjectNamePatternPlugin;
}

export default function ExternalReportObjectNamePattern({
    controlProps,
    elementProps,
}: FormElementControlPropsType<ExternalReportObjectNamePatternProps>) {
    const { t } = useBundleTranslation(['app/editor/element/extreport']);
    const plugin = controlProps.plugin ?? 'tableau';
    const defaultValue = plugin === 'oac' ? ':path / :item_name' : ':workbook_name / :view_name';
    const resetToDefault = () => {
        controlProps.form.hookFormSetValue(controlProps.name, defaultValue);
    };

    const [selectedValue, setSelectedValue] = useState('');
    useEffect(() => {
        const subscription = controlProps.form.hookFormWatch((value, { name, type }) => {
            if (name == 'external_report_list') {
                const a = getElementFieldValue(controlProps.form, 'external_report_list.selectedOption');
                //@ts-ignore
                if (a?.[0]?.label) {
                    //@ts-ignore
                    setSelectedValue(a[0].label);
                } else {
                    setSelectedValue('');
                }
            }
        });
        return () => subscription?.unsubscribe();
    }, [controlProps.form.hookFormWatch]);

    const showExample = () => {
        if (selectedValue == '') {
            alert(t('select_view'));
            return;
        }
        const namePattern = controlProps.form.hookFormGetValues(controlProps.name);
        let result: string;
        if (plugin === 'oac') {
            const lastSlashIndex = selectedValue.lastIndexOf('/');
            if (lastSlashIndex === -1) {
                // No namespace present - leave the name as is.
                result = selectedValue.trim();
            } else {
                const namespace = selectedValue.slice(0, lastSlashIndex).trim();
                const name = selectedValue.slice(lastSlashIndex + 1).trim();
                result = namePattern
                    .replace(new RegExp(':path', 'g'), namespace)
                    .replace(new RegExp(':item_name', 'g'), name);
            }
        } else {
            const parts = selectedValue.split('/');
            const workbook = parts.shift()?.trim();
            const view = parts.join('/').trim();
            result = namePattern
                .replace(new RegExp(':workbook_name', 'g'), workbook)
                .replace(new RegExp(':view_name', 'g'), view);
        }

        alert(result);
    };
    useEffect(() => {
        if (!controlProps?.form.formDidMount) {
            return;
        }
        resetToDefault();
    }, [controlProps.form.formDidMount]);

    // const v = controlProps.form.hookFormWatch('external_report_list');

    return (
        <>
            <FormText controlProps={controlProps} elementProps={elementProps} />
            <Stack spacing={2}>
                <Box sx={{ paddingTop: '8px' }}>
                    <Button
                        sx={{ marginRight: '8px' }}
                        variant={'outlined'}
                        startIcon={<IconMi icon="history" />}
                        onClick={() => resetToDefault()}
                    >
                        {t('revert_to_default')}
                    </Button>
                    <Button variant={'outlined'} onClick={() => showExample()}>
                        {t('show_example')}
                    </Button>
                </Box>
            </Stack>
        </>
    );
}

export class ExternalReportObjectNamePatternBuilder extends FormComponentBuilder {
    prepareProps(): ExternalReportObjectNamePatternProps {
        const plugin = this.elementProps.componentProps?.plugin;
        return {
            ...this.controlProps,
            plugin: ALLOWED_PLUGINS.includes(plugin) ? plugin : DEFAULT_PLUGIN,
        };
    }
}
